/**
 * Team Spain v0 — Vista de Calendario Compartido (M2)
 * 
 * Implementa vista mensual y vista de lista adaptada a móvil, navegación entre fechas,
 * creación y publicación de eventos, filtrado por estados/tipos y deep-linking por URL.
 */

import React, { useState, useEffect } from 'react'
import {
  Calendar as CalendarIcon,
  List,
  Plus,
  ChevronLeft,
  ChevronRight,
  Filter,
  MapPin,
  Sparkles,
} from 'lucide-react'
import { EventItem, EventType, EventStatus } from '../../types/calendar'
import { calendarService } from './calendarService'
import { EventDetailModal } from './EventDetailModal'
import { useAuth } from '../auth/AuthContext'
import { hasOperation } from '../auth/permissions'
import { Modal } from '../shared/Modal'

export const CalendarView: React.FC = () => {
  const { user } = useAuth()
  const [events, setEvents] = useState<EventItem[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Vistas: 'month' (mensual) o 'list' (lista para móvil)
  const [viewMode, setViewMode] = useState<'month' | 'list'>('month')

  // Fecha de navegación (mes seleccionado)
  // Por defecto iniciamos en Mayo 2026 si estamos explorando la demo o en la fecha actual
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    // Si la URL tiene un evento, o si es la demo, orientamos a mayo 2026 para ver la concentración
    return new Date(2026, 4, 1) // Mayo 2026 (mes 4 en JS 0-indexado)
  })

  // Filtros
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Evento seleccionado para modal de detalle / deep link
  const [selectedEventId, setSelectedEventId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('event') || null
  })

  // Modal para crear evento (solo staff)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createTitle, setCreateTitle] = useState('')
  const [createType, setCreateType] = useState<EventType>('practice')
  const [createStatus, setCreateStatus] = useState<EventStatus>('draft')
  const [createStart, setCreateStart] = useState('2026-05-20T18:00')
  const [createEnd, setCreateEnd] = useState('2026-05-20T20:00')
  const [createLocation, setCreateLocation] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false)

  const isCreateDirty = Boolean(createTitle.trim() || createLocation.trim() || createDescription.trim())

  // Rol de staff
  const isCoachOrAdmin = user?.memberships.some((m) => m.is_active && (!m.team_id || m.team_id === (user?.memberships.find((m) => m.is_active && m.team_id)?.team_id)) && hasOperation(m.roles, m.capabilities || [], 'manage_events'))

  const defaultTeamId =
    user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'
  const defaultOrgId =
    user?.memberships[0]?.organization_id || '11111111-1111-1111-1111-111111111111'
  const defaultSeasonId = '66666666-6666-6666-6666-666666666666'
  const fetchEvents = React.useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      const data = await calendarService.getEvents(defaultTeamId)
      setEvents(data)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar eventos del calendario.')
    } finally {
      setLoading(false)
    }
  }, [defaultTeamId])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  // Actualizar URL al seleccionar evento (Deep Linking)
  const handleOpenEvent = (id: string) => {
    setSelectedEventId(id)
    const url = new URL(window.location.href)
    url.searchParams.set('event', id)
    window.history.pushState({}, '', url.toString())
  }

  const handleCloseEvent = () => {
    setSelectedEventId(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('event')
    window.history.pushState({}, '', url.toString())
  }

  // Navegación de mes
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  const handleGoToday = () => {
    const today = new Date()
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1))
  }

  // Crear evento
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmittingCreate) return
    setIsSubmittingCreate(true)
    setErrorMsg(null)

    let newEv: EventItem | null = null
    try {
      newEv = await calendarService.createEvent({
        organization_id: defaultOrgId,
        team_id: defaultTeamId,
        season_id: defaultSeasonId,
        title: createTitle,
        event_type: createType,
        status: createStatus,
        start_time: new Date(createStart).toISOString(),
        end_time: new Date(createEnd).toISOString(),
        location: createLocation,
        description: createDescription,
      })

      // Convocatoria automática opcional de la plantilla si es staff
      try {
        await calendarService.summonTeamRoster(newEv.id, defaultTeamId)
      } catch (summonErr) {
        console.warn('Aviso: convocatoria automática omitida o fallida:', summonErr)
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al crear evento.')
      setIsSubmittingCreate(false)
      return
    }

    // A partir de aquí la escritura en BD fue exitosa
    setShowCreateModal(false)
    setCreateTitle('')
    setCreateLocation('')
    setCreateDescription('')
    setIsSubmittingCreate(false)

    // Recarga y apertura del detalle aislada de la escritura
    try {
      await fetchEvents()
      if (newEv) {
        handleOpenEvent(newEv.id)
      }
    } catch (fetchErr: unknown) {
      console.error('Error al actualizar lista de eventos tras la creación:', fetchErr)
      setErrorMsg('El evento se creó correctamente en el servidor, pero hubo un problema al actualizar la pantalla. Por favor recarga la vista.')
    }
  }

  // Filtrado de eventos
  const filteredEvents = events.filter((ev) => {
    if (typeFilter !== 'all' && ev.event_type !== typeFilter) return false
    if (statusFilter !== 'all' && ev.status !== statusFilter) return false
    return true
  })

  // Generador de días para vista mensual (Calendario 7 columnas: Lun-Dom)
  const getMonthDays = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDayIndex = new Date(year, month, 1).getDay()
    // En España la semana empieza en Lunes (0=Domingo -> 6, 1=Lunes -> 0)
    const startingOffset = (firstDayIndex + 6) % 7
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    const days: Array<{ dayNum: number; dateStr: string; isCurrentMonth: boolean; events: EventItem[] }> = []

    // Días del mes anterior para rellenar
    const prevMonthDays = new Date(year, month, 0).getDate()
    for (let i = startingOffset - 1; i >= 0; i--) {
      const d = prevMonthDays - i
      const prevDate = new Date(year, month - 1, d)
      const dateStr = prevDate.toISOString().split('T')[0]
      days.push({ dayNum: d, dateStr, isCurrentMonth: false, events: [] })
    }

    // Días del mes actual
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`

      // Encontrar eventos que ocurran en este día
      const dayEvents = filteredEvents.filter((ev) => {
        const evStart = ev.start_time.split('T')[0]
        const evEnd = ev.end_time.split('T')[0]
        return dateStr >= evStart && dateStr <= evEnd
      })

      days.push({ dayNum: d, dateStr, isCurrentMonth: true, events: dayEvents })
    }

    // Completar última semana si falta
    const remaining = (7 - (days.length % 7)) % 7
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(year, month + 1, i)
      const dateStr = nextDate.toISOString().split('T')[0]
      days.push({ dayNum: i, dateStr, isCurrentMonth: false, events: [] })
    }

    return days
  }

  const monthName = currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
  const todayStr = new Date().toISOString().split('T')[0]

  return (
    <div>
      {/* Encabezado Principal */}
      <div
        style={{
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.2rem' }}>Calendario de Actividades</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Concentraciones, entrenamientos, partidos y reuniones oficiales de la Selección.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          {/* Alternador de vista */}
          <div
            style={{
              display: 'flex',
              backgroundColor: 'var(--surface-card)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px',
            }}
          >
            <button
              type="button"
              onClick={() => setViewMode('month')}
              style={{
                padding: '0.35rem 0.65rem',
                fontSize: '0.78rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'month' ? 'var(--nav-bg-base)' : 'transparent',
                color: viewMode === 'month' ? '#ffffff' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}
            >
              <CalendarIcon size={14} />
              <span>Mes</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              style={{
                padding: '0.35rem 0.65rem',
                fontSize: '0.78rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'list' ? 'var(--nav-bg-base)' : 'transparent',
                color: viewMode === 'list' ? '#ffffff' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}
            >
              <List size={14} />
              <span>Lista</span>
            </button>
          </div>

          {isCoachOrAdmin && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
              style={{ fontSize: '0.82rem' }}
            >
              <Plus size={15} />
              <span>Nuevo Evento</span>
            </button>
          )}
        </div>
      </div>

      {/* Barra de Filtros y Navegación de Fechas */}
      <div
        className="card"
        style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevMonth}
            style={{ minHeight: '36px', minWidth: '36px', padding: 0 }}
            aria-label="Mes anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <span
            style={{
              fontWeight: 700,
              fontSize: '1rem',
              color: 'var(--text-main)',
              textTransform: 'capitalize',
              minWidth: '150px',
              textAlign: 'center',
            }}
          >
            {monthName}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleNextMonth}
            style={{ minHeight: '36px', minWidth: '36px', padding: 0 }}
            aria-label="Mes siguiente"
          >
            <ChevronRight size={18} />
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleGoToday}
            style={{ fontSize: '0.78rem', minHeight: '36px', padding: '0 0.65rem' }}
          >
            Hoy
          </button>

          {/* Selector dinámico de Concentraciones (data-driven sin fechas fijas) */}
          {events.filter((e) => e.event_type === 'camp').length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: '0.25rem' }}>
              <Sparkles size={14} style={{ color: '#d97706' }} />
              <select
                aria-label="Seleccionar concentración para navegar"
                style={{
                  fontSize: '0.78rem',
                  padding: '0.35rem 0.55rem',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid #f59e0b',
                  backgroundColor: '#fffbeb',
                  color: '#92400e',
                  fontWeight: 600,
                  cursor: 'pointer',
                  maxWidth: '220px',
                }}
                onChange={(e) => {
                  const campId = e.target.value
                  if (!campId) return
                  const selectedCamp = events.find((c) => c.id === campId)
                  if (selectedCamp) {
                    const d = new Date(selectedCamp.start_time)
                    setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1))
                    handleOpenEvent(selectedCamp.id)
                  }
                }}
                value=""
              >
                <option value="">Ir a concentración...</option>
                {events
                  .filter((e) => e.event_type === 'camp')
                  .map((camp) => {
                    const d = new Date(camp.start_time)
                    const monthLabel = d.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })
                    return (
                      <option key={camp.id} value={camp.id}>
                        {camp.title} ({monthLabel})
                      </option>
                    )
                  })}
              </select>
            </div>
          )}
        </div>

        {/* Filtros de Tipo y Estado */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            <Filter size={14} />
            <span>Tipo:</span>
            <select
              style={{
                fontSize: '0.78rem',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--surface-border-strong)',
                backgroundColor: 'var(--surface-card)',
              }}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">Todos los tipos</option>
              <option value="camp">Concentración</option>
              <option value="practice">Entrenamiento</option>
              <option value="game">Partido</option>
              <option value="meeting">Reunión</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            <span>Estado:</span>
            <select
              style={{
                fontSize: '0.78rem',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--surface-border-strong)',
                backgroundColor: 'var(--surface-card)',
              }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value="published">Publicados</option>
              {isCoachOrAdmin && <option value="draft">Borradores</option>}
              <option value="cancelled">Cancelados</option>
            </select>
          </div>
        </div>
      </div>

      {/* Avisos */}
      {errorMsg && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--status-danger-bg)',
            color: 'var(--status-danger-text)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--status-danger-border)',
            marginBottom: '1rem',
            fontSize: '0.82rem',
          }}
        >
          {errorMsg}
        </div>
      )}

      {/* CONTENIDO PRINCIPAL: VISTA MES O VISTA LISTA */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          Cargando eventos del calendario...
        </div>
      ) : viewMode === 'month' ? (
        /* VISTA MENSUAL (REJILLA 7 COLUMNAS) */
        <div className="table-container" style={{ margin: 0 }}>
          <div className="calendar-grid">
            <div className="calendar-day-header">Lun</div>
            <div className="calendar-day-header">Mar</div>
            <div className="calendar-day-header">Mié</div>
            <div className="calendar-day-header">Jue</div>
            <div className="calendar-day-header">Vie</div>
            <div className="calendar-day-header">Sáb</div>
            <div className="calendar-day-header">Dom</div>

            {getMonthDays().map((day, idx) => {
              const isToday = day.dateStr === todayStr
              return (
                <div
                  key={idx}
                  className={`calendar-day-cell ${!day.isCurrentMonth ? 'other-month' : ''} ${
                    isToday ? 'is-today' : ''
                  }`}
                  onClick={() => {
                    if (day.events.length > 0) {
                      handleOpenEvent(day.events[0].id)
                    }
                  }}
                >
                  <span className="calendar-day-num">{day.dayNum}</span>

                  {day.events.slice(0, 2).map((ev) => (
                    <div
                      key={ev.id}
                      className="calendar-event-pill"
                      style={{
                        backgroundColor:
                          ev.event_type === 'camp'
                            ? 'var(--brand-red-subtle)'
                            : ev.event_type === 'game'
                              ? 'var(--status-warning-bg)'
                              : 'var(--surface-subtle)',
                        color:
                          ev.event_type === 'camp'
                            ? 'var(--brand-red)'
                            : ev.event_type === 'game'
                              ? 'var(--status-warning-text)'
                              : 'var(--text-main)',
                        border: '1px solid var(--surface-border)',
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenEvent(ev.id)
                      }}
                      title={`${ev.title} (${ev.location})`}
                    >
                      {ev.status === 'draft' ? '[Borrador] ' : ev.status === 'cancelled' ? '[Cancelado] ' : ''}
                      {ev.title}
                    </div>
                  ))}

                  {day.events.length > 2 && (
                    <div className="calendar-event-more">
                      +{day.events.length - 2} más
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        /* VISTA DE LISTA (ÓPTIMA PARA MÓVIL) */
        <div className="table-container" style={{ margin: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '130px' }}>Fecha</th>
                <th>Evento</th>
                <th>Tipo</th>
                <th>Ubicación</th>
                <th style={{ width: '110px' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    No se encontraron eventos con los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                filteredEvents.map((ev) => (
                  <tr
                    key={ev.id}
                    onClick={() => handleOpenEvent(ev.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="numeric" style={{ fontWeight: 600 }}>
                      {new Date(ev.start_time).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{ev.title}</div>
                      {ev.description && (
                        <div
                          style={{
                            fontSize: '0.78rem',
                            color: 'var(--text-secondary)',
                            maxWidth: '360px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {ev.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-muted" style={{ textTransform: 'capitalize' }}>
                        {ev.event_type === 'camp'
                          ? 'Concentración'
                          : ev.event_type === 'practice'
                            ? 'Entrenamiento'
                            : ev.event_type === 'game'
                              ? 'Partido'
                              : ev.event_type === 'meeting'
                                ? 'Reunión'
                                : 'Otro'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <MapPin size={13} />
                        <span>{ev.location}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          ev.status === 'published'
                            ? 'badge-success'
                            : ev.status === 'cancelled'
                              ? 'badge-warning'
                              : 'badge-muted'
                        }`}
                      >
                        {ev.status === 'published'
                          ? 'Publicado'
                          : ev.status === 'cancelled'
                            ? 'Cancelado'
                            : 'Borrador'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Detalle / Deep Link */}
      {selectedEventId && (
        <EventDetailModal
          eventId={selectedEventId}
          onClose={handleCloseEvent}
          onEventUpdated={fetchEvents}
        />
      )}

      {/* Modal Crear Evento (Solo Staff) */}
      {showCreateModal && isCoachOrAdmin && (
        <Modal
          isOpen={true}
          onClose={() => setShowCreateModal(false)}
          title="Nuevo Evento en Calendario"
          subtitle="Programa una concentración, entrenamiento o partido para el equipo"
          size="md"
          hasUnsavedChanges={isCreateDirty}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="create-event-form"
                className="btn btn-primary"
                disabled={isSubmittingCreate}
              >
                {isSubmittingCreate ? 'Creando...' : 'Crear y Convocar'}
              </button>
            </>
          }
        >
          <form id="create-event-form" onSubmit={handleCreateSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Título del Evento *</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder="Ej. Sesión de Install Ofensivo"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Tipo de Evento</label>
                  <select
                    className="form-input"
                    value={createType}
                    onChange={(e) => setCreateType(e.target.value as EventType)}
                  >
                    <option value="camp">Concentración Nacional</option>
                    <option value="practice">Entrenamiento</option>
                    <option value="game">Partido / Competición</option>
                    <option value="meeting">Reunión de Staff</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Estado Inicial</label>
                  <select
                    className="form-input"
                    value={createStatus}
                    onChange={(e) => setCreateStatus(e.target.value as EventStatus)}
                  >
                    <option value="draft">Borrador (Solo Staff)</option>
                    <option value="published">Publicado (Visible a Convocados)</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Fecha y Hora de Inicio *</label>
                  <input
                    type="datetime-local"
                    required
                    className="form-input"
                    value={createStart}
                    onChange={(e) => setCreateStart(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Fecha y Hora de Fin *</label>
                  <input
                    type="datetime-local"
                    required
                    className="form-input"
                    value={createEnd}
                    onChange={(e) => setCreateEnd(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Ubicación</label>
                <input
                  type="text"
                  className="form-input"
                  value={createLocation}
                  onChange={(e) => setCreateLocation(e.target.value)}
                  placeholder="Ej. Polideportivo Los Cantos, Alcorcón"
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Descripción</label>
                <textarea
                  rows={3}
                  className="form-input"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  placeholder="Instrucciones generales para los convocados"
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
